import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEffect, useState } from 'react';
import { EntraGroup, useEntraGroups } from './useEntraGroups';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';

export const EntraGroupPickerFieldSchema = makeFieldSchema({
  output: z =>
    z.object({
      id: z.string().describe('The object id of the selected Entra group'),
      displayName: z.string().describe('The name of the selected Entra group'),
    }),
});

type EntraGroupPickerProps = typeof EntraGroupPickerFieldSchema.TProps;

const EntraGroupPicker = (props: EntraGroupPickerProps) => {
  const MINIMUM_LENGTH = 1;

  const {
    schema: { title = 'Entra group', description = 'Start typing to search for a group' },
    onChange,
    rawErrors,
    required,
    formData,
    errors,
  } = props;

  const getDisplayNameFromForm = (form: any) => form?.displayName ?? '';
  const getEntraGroupFromForm = (form: any) => (form ? (form as EntraGroup) : null);

  const [searchText, setSearchText] = useState(getDisplayNameFromForm(formData));
  const [entraGroup, setEntraGroup] = useState<EntraGroup | null>(getEntraGroupFromForm(formData));
  const { entraGroups, isLoading } = useEntraGroups(searchText, MINIMUM_LENGTH);

  // Update internal state based on formData from scaffolder
  useEffect(() => {
    setSearchText(getDisplayNameFromForm(formData));
    setEntraGroup(getEntraGroupFromForm(formData));
  }, [formData]);

  // Set searchText when text field changes
  const onSearchTextChange = (_: any, newValue: string) => {
    setSearchText(newValue);
    setEntraGroup(null);
  };

  // Set EntraGroup when selection changes
  const onSelectionChange = (_: any, newValue: string | EntraGroup | null) => {
    if (typeof newValue !== 'string') {
      setEntraGroup(newValue);
    }
  };

  // Update scaffolder data (same as formData) when entraGroup changes
  useEffect(() => {
    const isValid = entraGroup?.id && entraGroup?.displayName;
    onChange(isValid ? entraGroup : undefined);
  }, [entraGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      rawDescription={description}
      required={required}
      errors={errors}
    >
      <Autocomplete
        inputValue={searchText}
        onInputChange={onSearchTextChange}
        options={entraGroups || []}
        getOptionLabel={group => group?.displayName}
        onChange={onSelectionChange}
        loading={isLoading}
        renderInput={params => <TextField {...params} required={required} label={title} />}
        freeSolo
        selectOnFocus
        handleHomeEndKeys
        disableClearable
      />
    </ScaffolderField>
  );
};
export default EntraGroupPicker;
